import { useEffect, useState } from 'react';
import { Form, Formik } from 'formik';

import { DocumentSeriesResetPeriod } from '../../config/documentSeries';
import { NotificationType } from '../../config';
import useMenus from '../../context/MenuContext';
import documentSeriesService from '../../service/documentSeries.service';
import documentTypeService from '../../service/newDocumentType.service';
import addNotification from '../../utils/addNotification';
import Yup from '../../utils/yup';
import { DeleteDialogUI, FormBase, FormButtonPanelUI, FormHeaderUI } from '../Interface';
import DocumentSeriesDocTypeTable from './DocumentSeriesDocTypeTable';
import DocumentSeriesFields from './DocumentSeriesFields';
import DocumentSeriesList from './DocumentSeriesList';

const defaultValues = {
  id: null,
  name: '',
  format: '',
  nextNumber: 1,
  generated: false,
  resetPeriod: DocumentSeriesResetPeriod.NEVER,
  numberingYear: null,
  active: true,
  documentSeriesDocType: [],
};

const normalizeItem = (item) => ({
  ...defaultValues,
  ...item,
  documentSeriesDocType: (item?.documentSeriesDocType ?? []).map((link) => ({
    id: link.id ?? null,
    documentSeriesId: link.documentSeriesId ?? item?.id,
    docTypeId: Number(link.docTypeId),
    isDefault: Boolean(link.isDefault),
    documentType: link.documentType,
  })),
});

const DocumentSeriesPage = () => {
  const { getMenus, getMenuSubMenuId, getCanEdit, getCanDelete } = useMenus();
  const [selectedValues, setSelectedValues] = useState(defaultValues);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [rows, setRows] = useState({ rows: [], rowCount: 0 });
  const [viewField, setViewField] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [refetchTable, setRefetchTable] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const submenuId = getMenuSubMenuId('documentSeries');
  const canEdit = getCanEdit(submenuId);
  const canDelete = getCanDelete(submenuId);

  useEffect(() => {
    getMenus();
    documentTypeService.allDocumentTypes().then((data) => setDocumentTypes(Array.isArray(data) ? data : []));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const validationSchema = Yup.object().shape({
    name: Yup.string().trim().max(200).required(),
    format: Yup.string()
      .trim()
      .max(100)
      .matches(/\{N+\}/, 'A formátumnak tartalmaznia kell egy {NN...} sorszám helyőrzőt!')
      .required(),
    nextNumber: Yup.number().integer().min(1).required(),
    resetPeriod: Yup.string().oneOf(Object.values(DocumentSeriesResetPeriod)).required(),
    numberingYear: Yup.number()
      .transform((value, originalValue) => (originalValue === '' || originalValue === null ? null : value))
      .nullable()
      .integer()
      .min(2021, 'A számozási évnek 2020 utáninak kell lennie!'),
    active: Yup.boolean().required(),
    documentSeriesDocType: Yup.array().of(
      Yup.object().shape({
        docTypeId: Yup.number().integer().positive().required(),
        isDefault: Yup.boolean().required(),
      })
    ),
  });

  const loadItem = async (id, edit = false) => {
    const data = await documentSeriesService.getItem(id);
    const normalized = normalizeItem(data);
    setSelectedValues(normalized);
    setRowSelectionModel([normalized.id]);
    setViewField(!edit);
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setIsSaving(true);
    try {
      const payload = {
        ...(values.id ? { id: Number(values.id) } : {}),
        name: values.name.trim(),
        format: values.format.trim(),
        nextNumber: Number(values.nextNumber),
        resetPeriod: values.resetPeriod,
        numberingYear:
          values.numberingYear === null || values.numberingYear === '' ? null : Number(values.numberingYear),
        active: Boolean(values.active),
        documentSeriesDocType: (values.documentSeriesDocType ?? []).map((link) => ({
          ...(link.id ? { id: Number(link.id) } : {}),
          ...(values.id ? { documentSeriesId: Number(values.id) } : {}),
          docTypeId: Number(link.docTypeId),
          isDefault: Boolean(link.isDefault),
        })),
      };

      const data = values.id
        ? await documentSeriesService.updateItem(payload)
        : await documentSeriesService.createItem(payload);

      addNotification({
        content: values.id ? 'Sikeres módosítás!' : 'Sikeres mentés!',
        type: NotificationType.SUCCESS,
      });
      await loadItem(data.id);
      setRefetchTable(true);
    } finally {
      setIsSaving(false);
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete?.id || isSaving) return;
    setIsSaving(true);
    try {
      await documentSeriesService.deleteItem(itemToDelete.id);
      addNotification({ content: 'Sikeres törlés!', type: NotificationType.WARNING });
      setSelectedValues(defaultValues);
      setRowSelectionModel([]);
      setItemToDelete(null);
      setViewField(true);
      setRefetchTable(true);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <FormBase
        name="DocumentSeries"
        leftPanelContent={
          <div className="h-full overflow-auto custom-scrollbar">
            <FormHeaderUI
              addClick={() => {
                setSelectedValues(defaultValues);
                setRowSelectionModel([]);
                setViewField(false);
              }}
              addDisabled={!viewField || !canEdit || isSaving}
              modClick={() => setViewField(false)}
              modDisabled={!viewField || !selectedValues.id || !canEdit || isSaving}
              deleteClick={() => setItemToDelete(selectedValues)}
              deleteDisabled={!viewField || !selectedValues.id || !canDelete || isSaving}
            />
            <Formik
              initialValues={selectedValues}
              validationSchema={validationSchema}
              validateOnChange={false}
              enableReinitialize
              onSubmit={handleSubmit}
            >
              {({ resetForm, isSubmitting }) => (
                <Form>
                  <DocumentSeriesFields viewField={viewField} />
                  <DocumentSeriesDocTypeTable documentTypes={documentTypes} viewField={viewField} />
                  <FormButtonPanelUI
                    onCancelClick={() => {
                      setViewField(true);
                      resetForm();
                    }}
                    disabled={viewField || isSubmitting || isSaving}
                  />
                </Form>
              )}
            </Formik>
          </div>
        }
        rightPanelContent={
          <DocumentSeriesList
            data={{ rows, setRows }}
            refetch={{ refetchTable, setRefetchTable }}
            onSelect={loadItem}
            viewField={viewField}
            rowSelection={{ rowSelectionModel, setRowSelectionModel }}
          />
        }
      />
      <DeleteDialogUI
        show={Boolean(itemToDelete)}
        onHide={() => setItemToDelete(null)}
        handleRemoveElement={handleDelete}
        disabled={isSaving}
      />
    </>
  );
};

export default DocumentSeriesPage;
